import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import {
    Grid,
    Typography,
    Breadcrumbs,
    Link,
    Stack,
    Button,
    Box,
    Chip,
    Snackbar,
    Alert,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import RewardBadge from '../../widgets/RewardBadge';
import ImageWithFallback from '../../widgets/ImageWithFallback';

const PurchasePage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    useEffect(() => {
        const fetchPurchases = async () => {
            try {
                const data = await RequestService.getPurchases(user.company.id);
                setPurchases(data);
                setLoading(false);
            } catch (err) {
                setError('Erro ao carregar compras.');
                setLoading(false);
            }
        };

        fetchPurchases();
    }, []);

    const handleApproval = async (purchaseId, approve) => {
        try {
            await RequestService.approvePurchase(purchaseId, approve);

            // setPurchases((prevPurchases) =>
            //     prevPurchases.map((purchase) =>
            //         purchase.id === purchaseId
            //             ? { ...purchase, completed: approve }
            //             : purchase
            //     )
            // );
            setSnackbarMessage('Requisição aprovada com sucesso!');
            setSnackbarOpen(true);
            handleSuccessApprove();
        } catch (err) {
            console.error(
                `Erro ao ${approve ? 'aprovar' : 'reprovar'} a compra:`,
                err
            );
            alert(
                `Erro ao ${approve ? 'aprovar' : 'reprovar'} a compra. Tente novamente.`
            );
        }
    };

    const handleSuccessApprove = async () => {
        try {
            const data = await RequestService.getPurchases(user.company.id);
            setPurchases(data);
            setLoading(false);
        } catch (err) {
            setSnackbarMessage('Ocorreu um erro ao recarregar a página.');
            setSnackbarOpen(true);
        }
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    return (
        <Box>
            <Grid container justifyContent="start" sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Loja
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Pedidos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Typography
                sx={{
                    fontFamily: 'Poppins',
                    fontSize: 24,
                    fontWeight: '500',
                    color: '#0C3466',
                    mb: 2,
                }}
            >
                Pedidos
            </Typography>

            <Grid container spacing={2} gap={2}>
                {purchases.map((purchase) => (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={purchase.id}
                        sx={{
                            padding: 3,
                            borderRadius: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 350,
                            backgroundColor: '#EDF8FF',
                        }}
                    >
                        <Box elevation={3}>
                            <Stack sx={{ mb: 3 }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '16px',
                                        fontWeight: '400',
                                        color: '#292929',
                                    }}
                                >
                                    {purchase.user.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    Data do Pedido:{' '}
                                    {new Date(
                                        purchase.createdAt[0],
                                        purchase.createdAt[1] - 1,
                                        purchase.createdAt[2],
                                        purchase.createdAt[3],
                                        purchase.createdAt[4]
                                    ).toLocaleDateString('pt-BR')}{' '}
                                    -
                                    {new Date(
                                        purchase.createdAt[0],
                                        purchase.createdAt[1] - 1,
                                        purchase.createdAt[2],
                                        purchase.createdAt[3],
                                        purchase.createdAt[4]
                                    ).toLocaleTimeString('pt-BR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Typography>
                            </Stack>

                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '16px',
                                        fontWeight: '400',
                                        color: '#292929',
                                    }}
                                >
                                    Status:
                                </Typography>
                                <Chip
                                    label={
                                        purchase.completed
                                            ? 'Completo'
                                            : 'Pendente'
                                    }
                                    size="small"
                                />
                            </Stack>

                            <Typography
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    fontFamily: 'Poppins',
                                    fontSize: '16px',
                                    fontWeight: '400',
                                    color: '#292929',
                                }}
                            >
                                Pedido:
                            </Typography>
                            <Stack
                                direction="row"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                }}
                            >
                                <ImageWithFallback
                                    src={purchase.product.photo}
                                    alt={purchase.product.name}
                                    fallbackSrc="../../assets/images/gameup_dark.png"
                                    styles={{
                                        width: 100,
                                        height: 100,
                                        objectFit: 'cover',
                                        borderRadius: '10px',
                                        border: '1px solid #E0E0E0',
                                    }}
                                />
                                <Stack sx={{ ml: 2 }}>
                                    <Typography variant="body2">
                                        {purchase.product.name}
                                    </Typography>
                                    <RewardBadge
                                        icon={
                                            <CoinIcon
                                                style={{
                                                    color: '#134F99',
                                                    fontSize: '24px',
                                                }}
                                            />
                                        }
                                        value={purchase.product.price}
                                    />
                                </Stack>
                            </Stack>

                            <Box
                                sx={{
                                    backgroundColor: '#f7f9fc',
                                    padding: 2,
                                    borderRadius: 2,
                                    mb: 3,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                >
                                    Informações Importantes
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    • Se a recompensa estiver disponível no
                                    estoque, clique em Aprovar.
                                    <br />• A recompensa solicitada não está
                                    mais disponível? Clique em Cancelar e o
                                    aluno receberá o estorno do valor.
                                    <br />• Após a aprovação, o pedido estará
                                    disponível para entrega.
                                </Typography>
                            </Box>

                            {hasPermission('STORE_APPROVE') && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="space-between"
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            handleApproval(purchase.id, false)
                                        }
                                    >
                                        Reprovar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            handleApproval(purchase.id, true)
                                        }
                                    >
                                        Aprovar
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PurchasePage;
